// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useLedgerApiClient } from '../../contexts/LedgerApiContext';
import { useParties } from '../../contexts/PartiesContext';
import LedgerApiClientV2 from '../../utils/LedgerApiClientV2';
import { useLedgerEnd } from './ledger/useLedgerEnd';

/**
 * A generic hook for queries that depend on offset and operator.
 * This abstracts the common pattern used across many onboarding-related hooks.
 *
 * @param queryKey - The base query key (will be combined with offset and operator)
 * @param queryFn - A function that takes (ledgerApi, offset, operator) and returns the query result
 * @returns UseQueryResult with the query data
 *
 * @example
 * // For list queries:
 * const useComplianceServices = () =>
 *   useOperatorBasedQuery(
 *     'queryComplianceServices',
 *     (ledgerApi, offset, operator) => ledgerApi.queryComplianceServices(offset, operator)
 *   );
 *
 * // For single-item queries:
 * const useComplianceService = () =>
 *   useOperatorBasedQuery(
 *     'queryComplianceService',
 *     (ledgerApi, offset, operator) => ledgerApi.queryComplianceService(offset, operator)
 *   );
 */
export function useOperatorBasedQuery<T>(
  queryKey: string,
  queryFn: (ledgerApi: LedgerApiClientV2, offset: number, operator: string) => Promise<T>,
): UseQueryResult<T, string> {
  const ledgerApi = useLedgerApiClient();
  const { operator } = useParties();
  const offset = useLedgerEnd();

  return useQuery({
    // Note: ledgerApi is intentionally not included in the query key.
    // The ledgerApi instance may change (e.g., when reconnecting to wallet),
    // but this should not invalidate cached query results.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [queryKey, offset, operator],
    queryFn: async () => {
      return queryFn(ledgerApi, offset!, operator);
    },
    enabled: !!offset,
    refetchInterval: false,
  });
}

/**
 * A generic hook for queries that depend on offset, operator, and the current party.
 * This is used for single-item queries that find a specific service for the current user.
 *
 * @param queryKey - The base query key (will be combined with offset, operator, and party)
 * @param queryFn - A function that takes (ledgerApi, offset, operator, party) and returns the query result
 * @returns UseQueryResult with the query data
 *
 * @example
 * const useRegistrarService = () =>
 *   usePartyBasedQuery(
 *     'queryRegistryRegistrarService',
 *     (ledgerApi, offset, operator, party) => ledgerApi.queryRegistryRegistrarService(offset, operator, party)
 *   );
 */
export function usePartyBasedQuery<T>(
  queryKey: string,
  queryFn: (
    ledgerApi: LedgerApiClientV2,
    offset: number,
    operator: string,
    party: string,
  ) => Promise<T>,
): UseQueryResult<T, string> {
  const ledgerApi = useLedgerApiClient();
  const { operator, party } = useParties();
  const offset = useLedgerEnd();

  return useQuery({
    // Note: ledgerApi is intentionally not included in the query key.
    // The ledgerApi instance may change (e.g., when reconnecting to wallet),
    // but this should not invalidate cached query results.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [queryKey, offset, operator, party],
    queryFn: async () => {
      return queryFn(ledgerApi, offset!, operator, party);
    },
    enabled: !!offset,
    refetchInterval: false,
  });
}

/**
 * A generic hook for queries that depend on offset, operator, and a custom party.
 * This is used when the party comes from a different source than useParties() (e.g., useModuleParty).
 *
 * @param queryKey - The base query key (will be combined with offset, operator, and party)
 * @param party - The party value to use for the query (from a custom source)
 * @param queryFn - A function that takes (ledgerApi, offset, operator, party) and returns the query result
 * @returns UseQueryResult with the query data
 *
 * @example
 * const useClientOnboarderService = () => {
 *   const party = useModuleParty();
 *   return useCustomPartyQuery(
 *     'queryClientOnboarderService',
 *     party,
 *     (ledgerApi, offset, operator, p) => ledgerApi.queryClientOnboarderService(offset, operator, p)
 *   );
 * };
 */
export function useCustomPartyQuery<T>(
  queryKey: string,
  party: string,
  queryFn: (
    ledgerApi: LedgerApiClientV2,
    offset: number,
    operator: string,
    party: string,
  ) => Promise<T>,
): UseQueryResult<T, string> {
  const ledgerApi = useLedgerApiClient();
  const { operator } = useParties();
  const offset = useLedgerEnd();

  return useQuery({
    // Note: ledgerApi is intentionally not included in the query key.
    // The ledgerApi instance may change (e.g., when reconnecting to wallet),
    // but this should not invalidate cached query results.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [queryKey, offset, operator, party],
    queryFn: async () => {
      return queryFn(ledgerApi, offset!, operator, party);
    },
    enabled: !!offset,
    refetchInterval: false,
  });
}

export default useOperatorBasedQuery;
